import { createContext, useContext } from "react";
import type { usePortal, DDContext, DDStatus, RefDomainRow, RefResolved, RefValueRow } from "@app/portal-sdk";

/**
 * What every Due Diligence screen needs: the API, the client's reference lists read once at app load, the explanatory
 * content behind <Ref>, the caller's switches and where the application lives in the URL. The values this application
 * branches on are in lib/codes.ts and nowhere else.
 *
 * canManage here means "may open an engagement" (a client administrator or BKDP). What a person may do INSIDE an
 * engagement is the engagement's own access row (myLevel on every engagement response), never this switch.
 */
export type DdContextValue = {
  api: ReturnType<typeof usePortal>["api"];
  /** API base: /clients/:id/apps/due-diligence */
  base: string;
  /** URL base: /c/:slug/apps/due-diligence */
  root: string;
  canManage: boolean;
  isBkdp: boolean;
  status: DDStatus;
  codes: RefResolved;
  content: DDContext;
  options: (domain: string) => RefValueRow[];
  list: (domain: string) => RefDomainRow | undefined;
  listName: (domain: string) => string;
  label: (domain: string, code: string | null | undefined) => string | null;
  describe: (domain: string, code: string | null | undefined) => string | null;
  reloadStatus: () => Promise<void>;
};

export const DdCtx = createContext<DdContextValue | null>(null);
export const useDd = (): DdContextValue => {
  const ctx = useContext(DdCtx);
  if (!ctx) throw new Error("useDd must be used inside the Due Diligence app");
  return ctx;
};

/** The words this application settles (TERMINOLOGY.md), shown where they are first met. */
export const WORDS = {
  engagement: "An engagement is one diligence on one target: the workstreams you choose, the questions asked, what was received, and what it adds up to.",
  target: "Until the deal closes, this company exists only inside Due Diligence. No other application in the portal can see it.",
  codeName: "The code name is what lists show, and what the deal is called in front of anybody who is not on the deal team.",
  access: "Access is per engagement. A client administrator who is not on this list does not see the engagement anywhere.",
  bkdpSees: "BKDP sees every engagement and is not in this list.",
  outOfScope: "An out-of-scope review area is kept, grayed, with its reason, and the report says so in its own words: \"We did not look at it.\" That sentence is a finding in itself.",
  closed: "A closed engagement cannot be reopened, by anybody. The way back is a new engagement.",
  closing: "Closing records what happened and why. Every outcome but On hold makes the engagement read-only, for good.",
  dealStage: "The deal's own stage, not this engagement's. The two move independently.",
  library: "Library areas arrive with the application and are maintained by BKDP. Yours are maintained here.",
  yourPeople: "Your people: your own staff, recorded in Customer Management. A person may have no portal login; that is ordinary.",
  signIn: "People who can sign in: this client's portal users. A user may not be a recorded person; that is ordinary.",
  rating: "Ratings are shown as a distribution and a count, never as an average.",
  bank: "Every question your company can ask, in one place: the library's and your own. It is reused across engagements, which is the whole point of having one.",
  questions: "Per review area in scope, choose from your bank or write a new one. A question written here joins your bank as you write it.",
  coverage: "Coverage is a count of questions by lens, never a score. A review area with nothing on the risk lens is called out because that is usually an oversight.",
  evidence: "What you learned, recorded against the question it answers, with where it came from. Most answers come from documents, not meetings; an assumption is a source too, the weakest one, and it is marked wherever it surfaces.",
  participants: "The people on the other side and the advisors you talk to, and your own people on your side. Each carries the review areas they are expected to speak to, so the questions they are asked are theirs, not the whole bank.",
  sessions: "A conversation with a date, a mode and the people in it. Completing one says what was said is in; a correction reopens it and is recorded. A recording attached without consent recorded is flagged, never blocked.",
  findings: "What you now know, by workstream, each finding with the evidence behind it. A finding is published only with evidence and a rated review area; a published one is corrected, never edited. Ratings are a review area's; a verdict is the lead's judgement over them and is never computed."
};

/** HIE-5, HIE-7: what a member reads on the Structure screen instead of its controls. */
export const MAINTAINED_BANK = "Your hierarchy and its workbook are maintained by a client administrator. You can read the structure; ask an administrator to change it.";

/** Who may write, from the engagement's own access level (never from canManage). */
export const mayEdit = (level: string | null | undefined): boolean => level === "lead" || level === "contributor" || level === "bkdp";
export const mayLead = (level: string | null | undefined): boolean => level === "lead" || level === "bkdp";
